import { useCallback, useEffect, useMemo } from "react";

import { PalAutoComplete, PalButton, PalCheckbox, PalPage, PalTextField, PalTypography } from "@palamar/fe-library";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { MdFileCopy } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import styled from "@emotion/styled";
import { Grid } from "@mui/material";

import PRContainer from "~components/Generic/PRContainer";
import { userRoleOptions } from "~constants";
import DialogHelper from "~helpers/DialogHelper";
import HistoryHelper from "~helpers/HistoryHelper";
import {
  copyStatisticsFigure,
  createOrUpdateStatisticFigure,
  getStatisticFigure,
  setStatisticFigure,
} from "~store/statisticGenerator/actions";
import { selectStatisticFigure } from "~store/statisticGenerator/selectors";
import { selectCurrentProject, selectUserInfo } from "~store/user/selectors";

const StyledGridItem = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StatisticsFigureEdit = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const userInfo = useSelector(selectUserInfo);

  const currentProject = useSelector(selectCurrentProject);

  const statisticsFigure = useSelector(selectStatisticFigure);

  const isEditable = useMemo(() => {
    if (!statisticsFigure) return false;
    if (userInfo?.is_superuser) return true;
    if (statisticsFigure.is_public) return false;
    return true;
  }, [statisticsFigure, userInfo]);

  const handleClickCancel = useCallback(() => {
    HistoryHelper.goBack("/stats/settings/figures/", { scope: "dashboard" });
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: statisticsFigure?.id,
      figure_name: statisticsFigure?.figure_name || "",
      refresh_frequency: statisticsFigure?.refresh_frequency || "",
      is_public: statisticsFigure?.is_public || false,
      is_active: statisticsFigure?.is_active || false,
      user_spesific: statisticsFigure?.user_spesific || false,
      permissions: statisticsFigure?.permissions || [],
    },
    validationSchema: Yup.object({
      figure_name: Yup.string().required(t("component.formik.required").format(t("common.name"))),
      refresh_frequency: Yup.number()
        .typeError(t("component.formik.required").format(t("dashboard.statisticsFigureEdit.refreshFrequency")))
        .required(t("component.formik.required").format(t("dashboard.statisticsFigureEdit.refreshFrequency"))),
      is_public: Yup.boolean(),
      is_active: Yup.boolean(),
      user_spesific: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      await dispatch(createOrUpdateStatisticFigure(currentProject.id, values));
      handleClickCancel();
    },
  });

  const permissionOptions = useMemo(() => {
    return userRoleOptions.map((option) => ({
      label: t(option.label),
      value: option.value,
    }));
  }, [t]);

  const parentName = useMemo(
    () => [
      {
        label: t("dashboard.statisticsFigure"),
        url: "/stats/settings/figures/",
      },
      {
        label: id ? t("common.edit") : t("common.create"),
      },
    ],
    [id, t]
  );

  useEffect(() => {
    if (!id) return;
    dispatch(getStatisticFigure(currentProject.id, id));

    return () => {
      dispatch(setStatisticFigure(null));
    };
  }, [dispatch, id, currentProject.id]);

  const handleCopyFigure = useCallback(async () => {
    if (!id) return;
    if (
      !(await DialogHelper.showQuestionYesNo(
        t("dashboard.statisticsFigureEdit.copyFigureConfirmationTitle"),
        t("dashboard.statisticsFigureEdit.copyFigureConfirmationMessage")
      ))
    ) {
      return;
    }
    const result = await dispatch(copyStatisticsFigure(currentProject.id, id));
    // Get the duplicated figure ID from the result
    const duplicatedFigureId = result?.id;
    if (duplicatedFigureId) {
      HistoryHelper.push(`/stats/settings/figures/form/${duplicatedFigureId}`, { scope: "dashboard" });
    }
  }, [dispatch, id, t, currentProject.id]);

  const actions = useMemo(() => {
    return id
      ? [
          {
            label: t("dashboard.statisticsFigureEdit.duplicateFigure"),
            icon: MdFileCopy,
            color: "primary",
            onClick: handleCopyFigure,
          },
        ]
      : [];
  }, [id, t, handleCopyFigure]);

  return (
    <PRContainer
      actions={actions}
      description={t("dashboard.statisticsFigureEdit.description")}
      name={t("common.statistics")}
      parentName={parentName}
    >
      <PalPage>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PalTypography paddingLeft={2} variant="h4">
              {id ? t("dashboard.statisticsFigureEdit.edit") : t("dashboard.statisticsFigureEdit.create")}
            </PalTypography>
          </Grid>
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              disabled={!isEditable}
              invalid={formik.touched.figure_name && formik.errors.figure_name}
              label={t("dashboard.statisticsFigureEdit.figureName")}
              name="figure_name"
              value={formik.values.figure_name}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <PalTextField
              fullWidth
              disabled={!isEditable}
              invalid={formik.touched.refresh_frequency && formik.errors.refresh_frequency}
              label={t("dashboard.statisticsFigureEdit.refreshFrequency")}
              name="refresh_frequency"
              type="number"
              value={formik.values.refresh_frequency}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Grid>
          {userInfo?.is_superuser && (
            <>
              <Grid item md={6} xs={12}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={"auto"}>
                    <PalTypography>{t("dashboard.statisticsFigureEdit.isPublic")}:</PalTypography>
                  </Grid>
                  <Grid item>
                    <PalCheckbox
                      checked={formik.values.is_public}
                      disabled={!isEditable}
                      name="is_public"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={6} xs={12}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={"auto"}>
                    <PalTypography>{t("dashboard.statisticsFigureEdit.isActive")}:</PalTypography>
                  </Grid>
                  <Grid item>
                    <PalCheckbox
                      checked={formik.values.is_active}
                      disabled={!isEditable}
                      name="is_active"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={"auto"}>
                <PalTypography>{t("dashboard.statisticsFigureEdit.userSpecific")}:</PalTypography>
              </Grid>
              <Grid item>
                <PalCheckbox
                  checked={formik.values.user_spesific}
                  disabled={!isEditable}
                  name="user_spesific"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <PalAutoComplete
              fullWidth
              isPrimitiveValue
              multiple
              disabled={!isEditable}
              label={t("dashboard.statisticsFigureEdit.permissions")}
              name="permissions"
              options={permissionOptions}
              value={formik.values.permissions}
              onBlur={formik.handleBlur}
              onChange={(e, newValue) => formik.setFieldValue("permissions", newValue)}
            />
          </Grid>
          <StyledGridItem item xs={12}>
            <PalButton className="me-2" color="primary" variant="outlined" onClick={handleClickCancel}>
              {t("common.cancel")}
            </PalButton>
            <PalButton
              className="me-2"
              color="primary"
              disabled={!isEditable}
              variant="contained"
              onClick={formik.handleSubmit}
            >
              {t("common.submit")}
            </PalButton>
          </StyledGridItem>
        </Grid>
      </PalPage>
    </PRContainer>
  );
};

export default StatisticsFigureEdit;
